import { ApiTransport } from './ApiTransport';
import type {
  AzureWorkItemDetail,
  AzureWorkItemType,
  Issue,
  IssueQuery,
  IssueScope,
  IssueSearchResult,
  PaginatedWorkItems,
  ProviderId,
  SearchWorkItemsFilters,
} from './types';
import { extractError } from './apiErrors';
import { issueSearchParams, workItemQuery } from './boardQueries';

export class BoardsClient {
  constructor(private readonly transport: ApiTransport) {}

  async searchIssues(
    provider: ProviderId | 'jira',
    query: IssueQuery,
  ): Promise<IssueSearchResult> {
    const params = issueSearchParams(query);
    const response = await this.transport.request(
      `/boards/${encodeURIComponent(provider)}/issues?${params.toString()}`,
    );
    if (!response.ok) throw new Error('Failed to search issues');
    return response.json();
  }

  async getIssue(
    provider: ProviderId | 'jira',
    externalId: string,
    scope: IssueScope,
  ): Promise<Issue> {
    const params = new URLSearchParams({ owner: scope.owner });
    if (scope.project) params.set('project', scope.project);
    if (scope.repo) params.set('repo', scope.repo);
    const response = await this.transport.request(
      `/boards/${encodeURIComponent(provider)}/issues/${encodeURIComponent(
        externalId,
      )}?${params.toString()}`,
    );
    if (!response.ok) throw new Error('Failed to fetch issue');
    return response.json();
  }

  async listWorkItemTypes(projectId: string): Promise<AzureWorkItemType[]> {
    const response = await this.transport.request(
      `/azure/projects/${encodeURIComponent(projectId)}/workitem-types`,
    );
    if (!response.ok) {
      const msg = await extractError(
        response,
        'Failed to list work item types',
      );
      throw new Error(msg);
    }
    return response.json();
  }

  async searchWorkItems(
    projectId: string,
    filters?: Omit<SearchWorkItemsFilters, 'projectId'>,
  ): Promise<PaginatedWorkItems> {
    const qs = workItemQuery(filters);
    const response = await this.transport.request(
      `/azure/projects/${encodeURIComponent(projectId)}/workitems${qs}`,
    );
    if (!response.ok) {
      const msg = await extractError(response, 'Failed to search work items');
      throw new Error(msg);
    }
    return response.json();
  }

  async getWorkItem(
    projectId: string,
    workItemId: number,
  ): Promise<AzureWorkItemDetail> {
    const response = await this.transport.request(
      `/azure/projects/${encodeURIComponent(
        projectId,
      )}/workitems/${workItemId}`,
    );
    if (response.status === 404) {
      throw new Error('WORK_ITEM_NOT_FOUND');
    }
    if (response.status === 403) {
      throw new Error('AZURE_PERMISSION_DENIED');
    }
    if (!response.ok) {
      const msg = await extractError(response, 'Failed to get work item');
      throw new Error(msg);
    }
    return response.json();
  }
}
